const BASE_URL = '/api/v1';

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    ...((options?.headers as Record<string, string>) ?? {}),
  };

  // Only set Content-Type for requests that have a body
  if (options?.body) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${BASE_URL}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) notifyAuthRequired();
    const errorBody = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, errorBody.error ?? 'Request failed', errorBody);
  }

  return response.json();
}

/** Fired when the API rejects a request because the admin session is missing or expired. */
export const AUTH_REQUIRED_EVENT = 'admin:auth-required';

export function notifyAuthRequired(): void {
  window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
}

// --- Auth ---

export interface AuthStatus {
  authEnabled: boolean;
  authenticated: boolean;
}

export function fetchAuthStatus(): Promise<AuthStatus> {
  return request<AuthStatus>('/auth/me');
}

export function login(password: string): Promise<AuthStatus> {
  return request<AuthStatus>('/auth/login', { method: 'POST', body: JSON.stringify({ password }) });
}

export function logout(): Promise<AuthStatus> {
  return request<AuthStatus>('/auth/logout', { method: 'POST' });
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// --- Session types ---

export interface SessionSummary {
  updatedAt?: string;
  completionReason?: string | null;
  id: string;
  agentId: string;
  simulatedTime: string;
  status: 'active' | 'paused' | 'completed';
  cashBalance: number;
  createdAt: string;
}

export interface SessionDetail extends SessionSummary {
  initialCash: number;
  realizedPnl: number;
  replayMode: boolean;
  replayOffset: string | null;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
  };
}

// --- Import types ---

export interface StockImportParams {
  symbol: string;
  startDate?: string;
  endDate?: string;
  lookbackDays?: number;
  interval: '1min' | '5min' | '15min' | '30min' | '60min' | 'daily';
}

export interface CryptoImportParams {
  symbol: string;
  startDate?: string;
  endDate?: string;
  lookbackDays?: number;
  interval: '1min' | '5min' | '15min' | '30min' | '60min' | 'daily';
}

export interface ImportResult {
  importId: string;
  recordsImported: number;
  duplicatesSkipped: number;
  symbol: string;
  interval: string;
  dateRange: { start: string; end: string };
}

export interface ImportStatus {
  importId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  progress?: number;
  result?: ImportResult;
  error?: string;
}

// --- Symbol types ---

export interface Symbol {
  id: string;
  ticker: string;
  name: string;
  assetType: 'stock' | 'crypto' | 'forex';
  enabled: boolean;
  availableFrom: string | null;
  availableTo: string | null;
}

// --- API functions ---

export async function listSessions(
  page = 1,
  pageSize = 20
): Promise<PaginatedResponse<SessionSummary>> {
  return request<PaginatedResponse<SessionSummary>>(
    `/admin/sessions?page=${page}&pageSize=${pageSize}`
  );
}

export async function getSessionDetail(id: string): Promise<{ data: SessionDetail }> {
  return request<{ data: SessionDetail }>(`/admin/sessions/${id}`);
}

export async function closeSession(id: string): Promise<{ data: { success: boolean; message: string; reason: string } }> {
  return request<{ data: { success: boolean; message: string; reason: string } }>(`/admin/sessions/${id}`, {
    method: 'DELETE',
  });
}

// --- OHLCV data management ---

export interface OhlcvDeleteTarget {
  symbol: string;
  interval: string;
}

export interface OhlcvDeleteResult {
  deleted: Array<OhlcvDeleteTarget & { barsDeleted: number }>;
  totalBarsDeleted: number;
}

export async function deleteOhlcvData(targets: OhlcvDeleteTarget[]): Promise<{ data: OhlcvDeleteResult }> {
  return request<{ data: OhlcvDeleteResult }>('/admin/data/ohlcv', {
    method: 'DELETE',
    body: JSON.stringify({ targets }),
  });
}

export async function importStockData(params: StockImportParams): Promise<{ data: ImportResult }> {
  return request<{ data: ImportResult }>('/admin/import/stock', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function importCryptoData(params: CryptoImportParams): Promise<{ data: ImportResult }> {
  return request<{ data: ImportResult }>('/admin/import/crypto', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export interface ForexImportParams {
  symbol: string;
  startDate: string;
  endDate?: string;
}

export interface ForexImportStarted {
  importId: string;
  status: 'in_progress';
  symbol: string;
  dateRange: { start: string; end: string };
}

/** Starts a background Dukascopy import; progress shows in the import history. */
export async function importForexData(params: ForexImportParams): Promise<{ data: ForexImportStarted }> {
  return request<{ data: ForexImportStarted }>('/admin/import/forex', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/** Starts loading the trading school's data on the server (background). */
export async function importSchoolData(): Promise<{ data: { importId: string; status: 'in_progress' } }> {
  return request<{ data: { importId: string; status: 'in_progress' } }>('/admin/import/school-data', { method: 'POST' });
}

// --- New importer types ---

export interface FundamentalsImportParams {
  symbol: string;
  apiKey: string;
}

export interface FundamentalsImportResult {
  symbol: string;
  overviewImported: boolean;
  incomeStatements: number;
  balanceSheets: number;
  cashFlowStatements: number;
  earningsReports: number;
}

export interface NewsImportParams {
  symbol: string;
  startDate: string;
  endDate: string;
  apiKey: string;
}

export interface NewsImportResult {
  symbol: string;
  articlesImported: number;
  duplicatesSkipped: number;
}

export interface EconomicImportParams {
  indicator: 'real_gdp' | 'treasury_yield' | 'federal_funds_rate' | 'cpi' | 'inflation' | 'unemployment';
  startDate: string;
  endDate: string;
  fredApiKey: string;
}

export interface EconomicImportResult {
  indicator: string;
  dataPointsImported: number;
  duplicatesSkipped: number;
}

export interface CalendarImportParams {
  symbol?: string;
  apiKey: string;
}

export interface CalendarImportResult {
  eventsImported: number;
  duplicatesSkipped: number;
}

// --- New importer API functions ---

export async function importFundamentals(params: FundamentalsImportParams): Promise<{ data: FundamentalsImportResult }> {
  return request<{ data: FundamentalsImportResult }>('/admin/import/fundamentals', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function importNewsData(params: NewsImportParams): Promise<{ data: NewsImportResult }> {
  return request<{ data: NewsImportResult }>('/admin/import/news', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function importEconomicData(params: EconomicImportParams): Promise<{ data: EconomicImportResult }> {
  return request<{ data: EconomicImportResult }>('/admin/import/economic', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function importEarningsCalendar(params: CalendarImportParams): Promise<{ data: CalendarImportResult }> {
  return request<{ data: CalendarImportResult }>('/admin/import/calendar', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function uploadDataset(
  type: 'fundamentals' | 'news' | 'calendar',
  records: unknown[]
): Promise<{ data: ImportResult }> {
  return request<{ data: ImportResult }>(`/admin/upload/${type}`, {
    method: 'POST',
    body: JSON.stringify({ records }),
  });
}

export async function toggleSymbol(
  ticker: string,
  enabled: boolean
): Promise<{ data: Symbol }> {
  return request<{ data: Symbol }>(`/admin/symbols/${ticker}/toggle`, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
}

export async function setSymbolRange(
  ticker: string,
  from: string,
  to: string
): Promise<{ data: Symbol }> {
  return request<{ data: Symbol }>(`/admin/symbols/${ticker}/range`, {
    method: 'PUT',
    body: JSON.stringify({ from, to }),
  });
}

export async function getImportStatus(id: string): Promise<{ data: ImportStatus }> {
  return request<{ data: ImportStatus }>(`/admin/import/${id}/status`);
}

export async function listSymbols(): Promise<{ data: Symbol[] }> {
  return request<{ data: Symbol[] }>('/admin/symbols');
}

export interface ImportHistoryItem {
  importId: string;
  symbol: string;
  interval: string;
  status: 'completed' | 'failed' | 'in_progress';
  recordsImported: number;
  duplicatesSkipped: number;
  error?: string;
  completedAt: string;
}

export async function listImportHistory(): Promise<{ data: ImportHistoryItem[] }> {
  return request<{ data: ImportHistoryItem[] }>('/admin/imports');
}

// --- Order types ---

export interface Order {
  id: string;
  sessionId: string;
  symbol: string;
  side: 'buy' | 'sell';
  orderType: 'market' | 'limit';
  quantity: number;
  limitPrice: number | null;
  fillPrice: number | null;
  slippageAmount: number | null;
  status: 'pending' | 'filled' | 'cancelled' | 'rejected';
  extendedHours: boolean;
  takeProfit: number | null;
  stopLoss: number | null;
  tpSlStatus: 'active' | 'triggered_tp' | 'triggered_sl' | 'cancelled' | 'none';
  triggeredAt: string | null;
  triggerType: 'take_profit' | 'stop_loss' | null;
  closeOrderId: string | null;
  placedAt: string;
  filledAt: string | null;
}

// --- Portfolio types ---

export interface Position {
  symbol: string;
  quantity: number;
  avgEntryPrice: number;
  currentPrice: number;
  unrealizedPnl: number;
  marketValue: number;
}

export interface PortfolioState {
  cashBalance: number;
  positions: Position[];
  unrealizedPnl: number;
  realizedPnl: number;
  totalValue: number;
}

// --- API Usage types ---

export interface ApiUsageMetrics {
  totalRequests: number;
  categories: {
    timeSeries: number;
    indicators: number;
    trading: number;
    portfolio: number;
    marketData: number;
  };
  latestRequestAt: string | null;
}

// --- P&L Analysis types ---

export interface PnlAnalysis {
  realizedPnl: number;
  unrealizedPnl: number;
  totalReturnPercent: number;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  avgTradeReturnPercent: number;
}

// --- Session detail API functions ---

export async function getSessionOrders(sessionId: string): Promise<{ data: Order[] }> {
  return request<{ data: Order[] }>(`/admin/sessions/${sessionId}/orders`);
}

export async function getSessionPortfolio(sessionId: string): Promise<{ data: PortfolioState }> {
  return request<{ data: PortfolioState }>(`/admin/sessions/${sessionId}/portfolio`);
}

export async function getSessionApiUsage(sessionId: string): Promise<{ data: ApiUsageMetrics }> {
  return request<{ data: ApiUsageMetrics }>(`/admin/sessions/${sessionId}/api-usage`);
}

export async function getSessionPnlAnalysis(sessionId: string): Promise<{ data: PnlAnalysis }> {
  return request<{ data: PnlAnalysis }>(`/admin/sessions/${sessionId}/pnl-analysis`);
}

export async function getDashboardStats(): Promise<{
  data: { activeSessions: number; totalSessions: number; totalOrders: number };
}> {
  return request<{ data: { activeSessions: number; totalSessions: number; totalOrders: number } }>(
    '/admin/dashboard/stats'
  );
}

// --- Backtest (Strategy Builder) types ---

export interface BacktestSymbolCoverage {
  interval: string;
  barCount: number;
  from: string;
  to: string;
}

export interface BacktestSymbol {
  ticker: string;
  name: string;
  assetType: 'stock' | 'crypto' | 'forex';
  coverage: BacktestSymbolCoverage[];
}

export interface Scorecard {
  trades: number;
  winRate: number;
  avgWinPct: number;
  avgLossPct: number;
  avgTradePct: number;
  medianTradePct: number;
  profitFactor: number | null;
  profitFactorInfinite: boolean;
  expectancyPct: number;
  totalReturnPct: number;
  cagr: number;
  maxDrawdownPct: number;
  sharpe: number;
  sortino: number;
  calmar: number;
  exposurePct: number;
  longestLosingStreak: number;
  ratiosNotMeaningful: boolean;
  note?: string;
}

export interface TailDependence {
  top3ShareOfGrossProfit: number;
  profitFactorExclTop3: number;
  note?: string;
}

export interface EquityPoint {
  t: string;
  equity: number;
  drawdown: number;
}

export interface TradeRecord {
  symbol: string;
  side: 'long' | 'short';
  entryTime: string;
  entryPrice: number;
  exitTime: string | null;
  exitPrice: number | null;
  qty: number;
  pnl: number;
  returnPct: number;
  exitReason: string;
  barsHeld: number | null;
}

export interface RunManifest {
  dss_version: string;
  spec_hash: string;
  data_version: string;
  engine_version: string;
  generated_at: string;
  seed: number | null;
  costs: { slippage_pct: number | null; fees_pct: number | null };
}

export interface Bands {
  p05: number;
  p50: number;
  p95: number;
}

export interface RobustnessReport {
  walk_forward?: {
    windows: Array<{
      index: number;
      in_sample: { start: string; end: string; scorecard: Scorecard; trades: number };
      out_sample: { start: string; end: string; scorecard: Scorecard; trades: number };
    }>;
    out_of_sample: Scorecard;
    in_sample: Scorecard;
    note: string;
  };
  sweep?: {
    label: 'OPTIMIZATION';
    objective: string;
    rows: Array<{ params: Record<string, number>; objective: string; score: number; trades: number; oos_score: number | null }>;
    selected: Record<string, number> | null;
    note: string;
  };
  monte_carlo?: {
    method: string;
    runs: number;
    seed: number;
    total_return_pct: Bands;
    final_equity: Bands;
    max_drawdown_pct: Bands;
    totalReturnOrderInvariant: boolean;
    note: string;
  };
  crisis?: Array<{
    preset: string;
    start: string;
    end: string;
    scorecard: Scorecard;
    tail_dependence: TailDependence;
    trades: number;
  }>;
  tail_dependence: TailDependence;
}

export interface BacktestReport {
  manifest: RunManifest;
  summary: Scorecard;
  tail_dependence: TailDependence;
  equity_curve: EquityPoint[];
  trades: TradeRecord[];
  warnings: string[];
  robustness?: RobustnessReport;
}

export async function listBacktestSymbols(): Promise<{ data: BacktestSymbol[] }> {
  return request<{ data: BacktestSymbol[] }>('/admin/backtest/symbols');
}

export async function runBacktest(spec: unknown): Promise<{ data: BacktestReport }> {
  return request<{ data: BacktestReport }>('/admin/backtest/run', {
    method: 'POST',
    body: JSON.stringify({ spec }),
  });
}

// --- Exam ---

export interface ExamAttempt {
  spec_hash: string;
  strategy_name: string | null;
  holdout_start: string;
  passed: boolean;
  /** Lower target (50% of months); null for attempts recorded before it existed. */
  minimum_passed: boolean | null;
  total_months: number;
  profitable_months: number;
  profitable_months_pct: string | number;
  max_drawdown_pct: string | number;
  total_return_pct: string | number;
  attempted_at: string;
}

export interface ExamMonthResult {
  month: string;
  returnPct: number;
}

export interface ExamResult {
  passed: boolean;
  minimumPassed: boolean;
  attempt: number;
  specHash: string;
  holdoutStart: string;
  score: {
    passed: boolean;
    minimumPassed: boolean;
    profitableMonths: number;
    totalMonths: number;
    profitableMonthsPct: number;
    maxDrawdownPct: number;
    months: ExamMonthResult[];
    targets: {
      minProfitableMonthsPct: number;
      minimumProfitableMonthsPct: number;
      maxDrawdownPct: number;
    };
    tbills?: {
      returnPct: number;
      beaten: boolean;
    };
  };
  summary: {
    totalReturnPct: number;
    maxDrawdownPct: number;
    tradesCount?: number;
    sharpeRatio?: number;
    winRate?: number;
  };
  warnings: string[];
}

export async function listExamAttempts(): Promise<{ data: ExamAttempt[] }> {
  return request<{ data: ExamAttempt[] }>('/admin/backtest/exam/attempts');
}

export async function runExam(spec: unknown): Promise<{ data: ExamResult }> {
  return request<{ data: ExamResult }>('/admin/backtest/exam', {
    method: 'POST',
    body: JSON.stringify({ spec }),
  });
}

// ─── Admin Notifications & Member Approvals ───────────────────────────────────

export interface AdminNotificationConfig {
  notifyEmailTo: string;
  notifyEmailFrom: string;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  notifyNtfyTopic: string;
  notifyNtfyServer: string;
  enabledAlerts: {
    memberSignup: boolean;
    riskGovernor: boolean;
    examPassed: boolean;
    investorBooking: boolean;
  };
  isSmtpConfigured: boolean;
  hasSmtpPass: boolean;
}

export interface NotificationLogEntry {
  id: string;
  kind: string;
  title: string;
  recipient: string;
  channel: 'email' | 'ntfy' | 'console' | 'none';
  status: 'sent' | 'simulated' | 'failed';
  error?: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface MemberApplication {
  id: string;
  name: string;
  email: string;
  riskTier: string;
  targetInvestment: number;
  notes?: string;
  status: 'pending_approval' | 'approved' | 'rejected';
  inviteCode?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
}

export async function fetchNotificationConfig(): Promise<{ data: AdminNotificationConfig }> {
  return request<{ data: AdminNotificationConfig }>('/admin/notifications/config');
}

export async function updateNotificationConfig(
  update: Partial<AdminNotificationConfig> & { smtpPass?: string },
): Promise<{ success: boolean; data: AdminNotificationConfig }> {
  return request<{ success: boolean; data: AdminNotificationConfig }>('/admin/notifications/config', {
    method: 'POST',
    body: JSON.stringify(update),
  });
}

export async function testNotification(recipientEmail?: string): Promise<{ success: boolean; channel: string; message: string; error?: string }> {
  return request<{ success: boolean; channel: string; message: string; error?: string }>('/admin/notifications/test', {
    method: 'POST',
    body: JSON.stringify({ recipientEmail }),
  });
}

export async function fetchNotificationLogs(limit = 50): Promise<{ data: NotificationLogEntry[] }> {
  return request<{ data: NotificationLogEntry[] }>(`/admin/notifications/logs?limit=${limit}`);
}

export async function fetchMemberApplications(status?: string): Promise<{
  data: MemberApplication[];
  counts: { total: number; pending: number; approved: number; rejected: number };
}> {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return request<{
    data: MemberApplication[];
    counts: { total: number; pending: number; approved: number; rejected: number };
  }>(`/admin/members/applications${q}`);
}

export async function approveMemberApplication(
  id: string,
): Promise<{ success: boolean; message: string; inviteCode: string; data: MemberApplication }> {
  return request<{ success: boolean; message: string; inviteCode: string; data: MemberApplication }>(
    `/admin/members/applications/${id}/approve`,
    {
      method: 'POST',
    },
  );
}

export async function rejectMemberApplication(
  id: string,
  reason?: string,
): Promise<{ success: boolean; message: string; data: MemberApplication }> {
  return request<{ success: boolean; message: string; data: MemberApplication }>(
    `/admin/members/applications/${id}/reject`,
    {
      method: 'POST',
      body: JSON.stringify({ reason }),
    },
  );
}


